/**
 * Configuration du client généré — spec §6.2.
 *
 * C'est le SEUL endroit du code qui parle à `fetch` pour `/api`. Il installe :
 *  1. la baseUrl runtime ;
 *  2. le Bearer (avec rafraîchissement opportuniste) sur chaque requête ;
 *  3. UN rejeu sur 401 après rafraîchissement forcé, puis `login()` ;
 *  4. la conversion de TOUT échec en `ApiError`.
 */
import { client } from '@/api/generated/client.gen';
import { ensureFreshToken, getToken, login } from '@/auth/keycloak';
import { ApiError, isErrorCode } from './errors';
import type { ApiErrorParams } from './errors';
import { NETWORK_ERROR_LABEL } from './messages';

/**
 * Clones pris AVANT l'envoi : dans l'intercepteur de réponse le body est déjà
 * consommé, et tout rejeu de POST/PUT perdrait sa charge utile.
 */
const pendingClones = new WeakMap<Request, Request>();

/** Marqueur privé des requêtes déjà rejouées — un seul rejeu, jamais de boucle. */
const replayedRequests = new WeakSet<Request>();

function authorizationHeader(): string {
  return `Bearer ${getToken() ?? ''}`;
}

/** Au-delà, un corps d'erreur textuel est une page, pas un message serveur. */
const MAX_SERVER_MESSAGE_LENGTH = 300;

/**
 * Corps d'erreur de Hello-Back (`ApiErrorBody`).
 *
 * ⚠️ Le client généré fait `throw jsonError ?? textError` : `error` peut donc
 * être un corps JSON Hello-Back, mais AUSSI une chaîne brute (page HTML d'un 502 nginx
 * ou d'un proxy Vite quand Hello-Back est arrêté) ou une erreur JS (un
 * `SyntaxError` de `JSON.parse` quand nginx sert `index.html` sur `/api`). Seule
 * la première forme porte un code.
 */
function isServerErrorBody(value: unknown): value is {
  statusCode: number; statusMessage?: unknown; message: string; code?: unknown; params?: unknown;
} {
  if (typeof value !== 'object' || value === null) return false;
  const raw = value as Record<string, unknown>;
  return typeof raw.statusCode === 'number' && typeof raw.message === 'string';
}

/** Objet plat de `string | number`, sinon null. */
function errorParams(value: unknown): ApiErrorParams | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const entries = Object.entries(value);
  if (!entries.every(([, param]) => typeof param === 'string' || typeof param === 'number')) return null;
  return Object.fromEntries(entries) as ApiErrorParams;
}

/** Un corps textuel n'est retenu que s'il ressemble à un message, pas à une page. */
function textMessage(error: unknown): string | null {
  if (typeof error !== 'string') return null;
  const text = error.trim();
  if (text.length === 0 || text.length > MAX_SERVER_MESSAGE_LENGTH) return null;
  if (text.startsWith('<')) return null;
  return text;
}

/** Normalise en `ApiError` le corps d'erreur de Hello-Back, ou l'échec réseau. */
export function toApiError(error: unknown, response: Response | undefined): ApiError {
  if (error instanceof ApiError) return error;

  // Pas de réponse = échec réseau (ou construction de requête impossible).
  if (!response) {
    return new ApiError(0, 'Network Error', NETWORK_ERROR_LABEL, error);
  }

  const body = isServerErrorBody(error) ? error : null;

  const statusCode = body ? body.statusCode : response.status;
  const statusMessage = body && typeof body.statusMessage === 'string' && body.statusMessage.length > 0
    ? body.statusMessage
    : (response.statusText || 'Error');

  const serverMessage = (body && body.message.length > 0 ? body.message : null) ?? textMessage(error);
  const message = serverMessage ?? `${statusCode} ${statusMessage}`;
  const code = body && isErrorCode(body.code) ? body.code : null;
  const params = body ? errorParams(body.params) : null;

  // `body` garde TOUJOURS le brut (HTML, SyntaxError…) pour le débogage.
  return new ApiError(statusCode, statusMessage, message, error, serverMessage === null, code, params);
}

export function setupHttp(baseUrl: string): void {
  client.setConfig({ baseUrl });

  client.interceptors.request.use(async (request) => {
    // Rafraîchissement opportuniste : no-op si le token a plus de 70 s de vie.
    // Résout `false` quand le rafraîchissement est impossible — on laisse quand
    // même partir la requête, c'est le 401 qui déclenchera la suite.
    await ensureFreshToken(70);
    request.headers.set('Authorization', authorizationHeader());
    pendingClones.set(request, request.clone());
    return request;
  });

  client.interceptors.response.use(async (response, request, options) => {
    if (response.status !== 401) return response;
    if (replayedRequests.has(request)) return response;

    const clone = pendingClones.get(request);
    if (!clone) return response;

    // Rafraîchissement FORCÉ, partagé entre tous les 401 concurrents.
    // Son contrat interdit la rejection (§5.1) : pas de try/catch ici.
    const refreshed = await ensureFreshToken(-1);
    if (!refreshed) {
      // Session morte : on part sur Keycloak et on rend le 401 tel quel,
      // la navigation de premier niveau étant déjà lancée.
      void login();
      return response;
    }

    const retry = clone.clone();
    retry.headers.set('Authorization', authorizationHeader());
    replayedRequests.add(request);
    replayedRequests.add(retry);

    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    return doFetch(retry);
  });

  client.interceptors.error.use((error, response) => toApiError(error, response));
}
